import { NonAppNavWrapper } from '@/components/generic/non-app-nav'
import { Button } from '@/components/ui/button'
import { ContainerScroll } from '@/components/ui/container-scroll-animation'
import { Input } from '@/components/ui/input'
import { useSavedUrl } from '@/hooks/use-saved-url'
import { useAuth } from '@clerk/nextjs'
import { zodResolver } from '@hookform/resolvers/zod'
import { CornerRightUp, Sparkles } from 'lucide-react'
import { NextSeo } from 'next-seo'
import { Inter } from 'next/font/google'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

export const companies = [
  {
    name: 'Starbucks',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/starbucks.png',
    industry: 'Food & Beverage',
    headquarters: 'Seattle, WA',
    website: 'https://www.starbucks.com',
    tag: 'Analyzing market dominance, expansion strategies, and sensitivity to trends',
  },
  {
    name: 'Microsoft',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/microsoft.png',
    industry: 'Software',
    headquarters: 'Redmond, WA',
    website: 'https://www.microsoft.com',
    tag: 'Evaluating ecosystem integration, innovation, and competitive threats',
  },
  {
    name: 'Apple Inc.',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/apple.png',
    industry: 'Technology',
    headquarters: 'Cupertino, CA',
    website: 'https://www.apple.com',
    tag: 'Assessing product innovation, market positioning, and supply chain risks',
  },
  {
    name: 'IBM',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/ibm.png',
    industry: 'Technology',
    headquarters: 'Armonk, NY',
    website: 'https://www.ibm.com',
    tag: 'Exploring technology leadership, service diversification, and adaptation challenges',
  },
  {
    name: 'Nike',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/nike.png',
    industry: 'Apparel',
    headquarters: 'Beaverton, OR',
    website: 'https://www.nike.com',
    tag: 'Investigating brand strength, innovation in design, and market competition',
  },
  {
    name: 'Tesla, Inc.',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/tesla.png',
    industry: 'Automotive',
    headquarters: 'Palo Alto, CA',
    website: 'https://www.tesla.com',
    tag: 'Scrutinizing innovation leadership, manufacturing scalability, and regulatory hurdles',
  },
  {
    name: 'Amazon',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/amazon.png',
    industry: 'E-commerce',
    headquarters: 'Seattle, WA',
    website: 'https://www.amazon.com',
    tag: 'Reviewing e-commerce dominance, growth potential, and logistic challenges',
  },
  {
    name: 'Netflix',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/netflix.png',
    industry: 'Entertainment',
    headquarters: 'Los Gatos, CA',
    website: 'https://www.netflix.com',
    tag: 'Assessing content innovation, global reach, and competitive streaming landscape',
  },
  {
    name: 'Walmart',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/walmart.png',
    industry: 'Retail',
    headquarters: 'Bentonville, AR',
    website: 'https://www.walmart.com',
    tag: 'Analyzing retail supremacy, digital transformation, and market adaptability',
  },
  {
    name: 'Adidas',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/adidas.png',
    industry: 'Apparel',
    headquarters: 'Herzogenaurach, Germany',
    website: 'https://www.adidas.com',
    tag: 'Examining brand heritage, innovation in sustainability, and competitive pressures',
  },
  {
    name: 'Coca-Cola',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/coca-cola.png',
    industry: 'Beverages',
    headquarters: 'Atlanta, GA',
    website: 'https://www.coca-cola.com',
    tag: 'Evaluating global brand recognition, product diversity, and health trend impacts',
  },
  {
    name: 'Typeform',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/typeform.png',
    industry: 'Software',
    headquarters: 'Barcelona, Spain',
    website: 'https://www.typeform.com',
    tag: 'Investigating user engagement strengths, innovation in UI/UX, and market penetration',
  },
  {
    name: 'Figma',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/figma.png',
    industry: 'Software',
    headquarters: 'San Francisco, CA',
    website: 'https://www.figma.com',
    tag: 'Reviewing collaboration tool impact, design community integration, and scalability',
  },
  {
    name: 'Slack',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/slack.png',
    industry: 'Software',
    headquarters: 'San Francisco, CA',
    website: 'https://www.slack.com',
    tag: 'Assessing communication platform innovation, adoption rates, and competitive threats',
  },
  {
    name: 'Airbnb',
    designation: 'SWOT Analysis Report',
    image: '/images/screenshots/airbnb.png',
    industry: 'Hospitality',
    headquarters: 'San Francisco, CA',
    website: 'https://www.airbnb.com',
    tag: 'Exploring market disruption, scalability of the business model, and regulatory challenges',
  },
]

const inter = Inter({ subsets: ['latin'] })

const urlSchema = z.object({
  url: z.string(),
})

type UrlForm = z.infer<typeof urlSchema>

export default function Home() {
  const router = useRouter()
  const { isSignedIn } = useAuth()

  console.log(router.query)

  const { data: url } = useSavedUrl()

  const form = useForm<UrlForm>({
    resolver: zodResolver(urlSchema),
    defaultValues: {
      url: url || '',
    },
  })

  const { register, setValue, setFocus } = form

  React.useEffect(() => {
    setFocus('url')
  }, [setFocus])

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault()
    router.push(isSignedIn ? '/pricing' : '/auth/signin')
  }

  return (
    <>
      <NextSeo
        title="v0 report - Unleash the power of Automated SWOT Generation"
        description="Upload files, customize sections, and harness our advanced web scraping technology for a detailed SWOT analysis."
      />
      <NonAppNavWrapper>
        <main className={`scroll-smooth flex min-h-screen  flex-col ${inter.className}`}>
          <div className="mt-60 md:mb-20 text-sm">
            <p className="mx-auto mb-2 w-9/12 md:w-4/12 text-center md:hidden block">
              Enter a url to generate a report
            </p>
            <form
              className="shadow-lg shadow-sky-300/70 hover:shadow-sky-300/90 focus-within:shadow-sky-300/90 text-white mx-auto w-9/12 md:w-4/12 flex items-center space-x-2 px-2 py-1 mb-7 bg-zinc-900 rounded-full"
              onSubmit={handleSubmit}
            >
              <Sparkles className="text-fray-500 ml-2 w-6 h-6" />
              <Input
                type="text"
                placeholder="What company do you want a report on?"
                className="bg-zinc-900 flex-1 rounded-xl text-md border-transparent focus:border-zinc-900 focus:outline-none autofill-bg focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0"
                {...register('url', {
                  onChange: (e) => {
                    localStorage.setItem('url', e.target.value)
                  },
                })}
              />
              <Link
                href={isSignedIn ? '/pricing' : '/auth/signin'}
                className="flex items-center space-x-2  bg-zinc-900 hover:bg-zinc-900 text-zinc-400 hover:text-white rounded-full"
                aria-label={'Get a SWOT Analysis'}
              >
                <CornerRightUp className="w-4 h-4 mr-2" />
              </Link>
            </form>
            <p className="select-none mx-auto w-9/12 md:w-4/12 text-center hidden md:block text-zinc-500">
              Enter a url to generate a report. eg{' '}
              <span
                className="cursor-pointer hover:underline hover:text-sky-500 hover:underline"
                onClick={() => {
                  setValue('url', 'https://www.netflix.com')
                  setFocus('url')
                }}
              >
                netflix.com
              </span>
            </p>
          </div>
          <div className="mt-4 flex flex-col overflow-hidden">
            <ContainerScroll
              companies={companies}
              titleComponent={
                <h1 className="text-4xl font-semibold text-black">
                  Unleash the power of
                  <br />
                  <span className="text-4xl md:text-[5rem] font-bold mt-10 leading-none">
                    Automated SWOT Generation
                  </span>
                </h1>
              }
            />
          </div>

          <div className="mx-auto mb-24">
            <h2 className="w-fit px-2 mx-auto text-center text-3xl font-bold mb-10">
              How does v0 SWOT generator work?
            </h2>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 w-11/12 md:w-2/3 mx-auto mb-24">
              <div className="shadow-lg p-6 flex flex-col bg-white rounded-xl h-fit border">
                <h3 className="font-semibold mb-1">1. We gather data for you</h3>
                <p>
                  We automatically collect data from multiple sources and ensure your SWOT uses the
                  most current and comprehensive information.
                </p>
              </div>

              <div className="shadow-lg p-6 flex flex-col bg-white rounded-xl h-fit border">
                <h3 className="font-semibold mb-1">2. You provide relevant files</h3>
                <p>
                  You have the option to upload relevant documents and data to enrich the SWOT
                  reports with your specific insights and analysis.
                </p>
              </div>

              <div className="shadow-lg p-6 flex flex-col bg-white rounded-xl h-fit border">
                <h3 className="font-semibold mb-1">3. We write a report for you</h3>
                <p>
                  Our AI-powered report generator creates a professional SWOT analysis report using
                  the data you provide and the data we gather.
                </p>
              </div>

              <div className="shadow-lg p-6 flex flex-col bg-white rounded-xl h-fit border">
                <h3 className="font-semibold mb-1">4. Refine your reports </h3>
                <p>
                  Refine your SWOT reports effortlessly with a Notion-style editor, ensuring
                  professional quality and impactful content.
                </p>
              </div>
            </div>
          </div>

          <section className="w-11/12 mx-auto bg-white mb-48 md:w-fit drop-shadow-lg rounded-3xl border">
            <div className="py-8 px-4 mx-auto max-w-screen-xl sm:py-16 lg:px-6">
              <div className="mx-auto max-w-screen-sm text-center">
                <h2 className="mb-4 text-4xl tracking-tight font-bold leading-tight text-gray-900">
                  Get your first SWOT report
                </h2>
                <p className="mb-6 font-light text-gray-500 md:text-lg">
                  Get instant access and start generating professional SWOT analysis reports for
                  your business.
                </p>
                <Button onClick={() => router.push('/auth/signup')}>
                  Get a SWOT Analysis Report
                </Button>
              </div>
            </div>
          </section>
        </main>
      </NonAppNavWrapper>
    </>
  )
}
